// src/components/ProfileBannerDrawer.js

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import './ProfileBannerDrawer.css';

import placeholderProfile from '../assets/placeholder-profile.png';
import QuickTopUpPopup from './popups/QuickTopUpPopup';

import {
  startTrial,
  purchaseBanner,
  equipBanner,
  unequipBanner,
} from '../services/profileBannerApi';

import {
  trackBannerPreviewOpen,
} from '../services/revenueAnalytics';

const getBannerId = (banner) => {
  return banner?._id || banner?.id || banner?.bannerId || '';
};

const getResponseBanner = (data, fallbackBanner = null) => {
  return (
    data?.equippedProfileBanner ||
    data?.equippedBanner ||
    data?.banner ||
    data?.entitlement?.banner ||
    fallbackBanner ||
    null
  );
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong. Please try again.'
  );
};

const isWalletFundingError = (error) => {
  const code = String(
    error?.response?.data?.code || ''
  ).toUpperCase();

  const action = String(
    error?.response?.data?.action || ''
  ).toUpperCase();

  const backendMessage = String(
    error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      ''
  ).toLowerCase();

  return (
    code === 'INSUFFICIENT_PI' ||
    code === 'WALLET_NOT_FOUND' ||
    code === 'INSUFFICIENT_BALANCE' ||
    code === 'WALLET_INSUFFICIENT_BALANCE' ||
    code === 'WALLET_NOT_ACTIVE' ||
    code === 'INACTIVE_WALLET' ||
    action === 'TOP_UP_REQUIRED' ||
    backendMessage.includes('insufficient') ||
    backendMessage.includes('not enough pi') ||
    backendMessage.includes('no active barterfi wallet') ||
    backendMessage.includes('wallet not found') ||
    backendMessage.includes('wallet is not active') ||
    backendMessage.includes('inactive wallet') ||
    backendMessage.includes('insufficient balance')
  );
};

const getBannerStatus = (banner) => {
  if (banner?.equipped) {
    return {
      label: 'Equipped',
      className: 'is-equipped',
    };
  }

  if (
    banner?.owned ||
    banner?.accessType === 'purchased' ||
    banner?.status === 'owned'
  ) {
    return {
      label: 'Owned',
      className: 'is-owned',
    };
  }

  if (
    banner?.trialActive ||
    (
      banner?.accessType === 'trial' &&
      banner?.status !== 'expired'
    )
  ) {
    return {
      label: 'Trial',
      className: 'is-trial',
    };
  }

  if (
    banner?.trialExpired ||
    banner?.status === 'expired'
  ) {
    return {
      label: 'Expired',
      className: 'is-expired',
    };
  }

  return null;
};

const getStoredUser = () => {
  try {
    return JSON.parse(
      localStorage.getItem('barterfi_user') || 'null'
    );
  } catch (error) {
    console.warn(
      'Could not read stored BarterFi user:',
      error
    );

    return null;
  }
};

function ProfileBannerDrawer({
  isOpen,
  banner,
  banners = [],
  user,
  trialUsed = false,
  onSelectBanner,
  onClose,
  onBannerStateChange,
}) {

const navigate = useNavigate();

  const [workingAction, setWorkingAction] = useState('');
  const [actionError, setActionError] = useState('');
  const [showQuickTopUp, setShowQuickTopUp] = useState(false);

  const selectorItemRefs = useRef({});
  const quickTopUpTimerRef = useRef(null);

  const bannerId = getBannerId(banner);

  const storedUser = getStoredUser();

  const walletUsername =
    user?.username ||
    storedUser?.username ||
    '';

  const selectorBanners = useMemo(() => {
    if (!Array.isArray(banners)) {
      return banner ? [banner] : [];
    }

    const sortedBanners = [...banners].sort((a, b) => {
      const firstOrder = Number(a?.sortOrder || 0);
      const secondOrder = Number(b?.sortOrder || 0);

      return firstOrder - secondOrder;
    });

    if (!bannerId) {
      return sortedBanners;
    }

    const selectedExists = sortedBanners.some(
      (item) =>
        String(getBannerId(item)) === String(bannerId)
    );

    if (selectedExists) {
      return sortedBanners;
    }

    return banner
      ? [banner, ...sortedBanners]
      : sortedBanners;
  }, [banners, banner, bannerId]);

  const isOwned = Boolean(
    banner?.owned ||
      banner?.accessType === 'purchased' ||
      banner?.status === 'owned'
  );

  const isEquipped = Boolean(banner?.equipped);

  const isTrialActive = Boolean(
    banner?.trialActive ||
      (
        banner?.accessType === 'trial' &&
        banner?.status !== 'expired'
      )
  );

  const isTrialExpired = Boolean(
    banner?.trialExpired ||
      banner?.status === 'expired'
  );

  const canStartTrial = Boolean(
    !isOwned &&
      !isTrialActive &&
      !isTrialExpired &&
      !trialUsed &&
      banner?.canStartTrial !== false
  );

  const pricePi = Number(banner?.pricePi || 5);

  const isWorking = Boolean(workingAction);

  const clearQuickTopUpTimer = () => {
    if (quickTopUpTimerRef.current) {
      window.clearTimeout(quickTopUpTimerRef.current);
      quickTopUpTimerRef.current = null;
    }
  };

  const openQuickTopUpAfterDelay = () => {
    clearQuickTopUpTimer();

    quickTopUpTimerRef.current = window.setTimeout(() => {
      setShowQuickTopUp(true);
      quickTopUpTimerRef.current = null;
    }, 2500);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setWorkingAction('');
      setActionError('');
      setShowQuickTopUp(false);
      clearQuickTopUpTimer();
    }
  }, [isOpen]);

  useEffect(() => {
    setActionError('');
  }, [bannerId]);

  useEffect(() => {
    if (!isOpen || !bannerId || !banner) return;

    void trackBannerPreviewOpen(banner);
  }, [isOpen, bannerId, banner]);

  useEffect(() => {
    if (!isOpen || !bannerId) return;

    const selectedElement =
      selectorItemRefs.current[String(bannerId)];

    if (!selectedElement) return;

    const frameId = window.requestAnimationFrame(() => {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen, bannerId]);

  useEffect(() => {
    return () => {
      clearQuickTopUpTimer();
    };
  }, []);

  const actionState = useMemo(() => {
    if (isOwned && isEquipped) {
      return {
        type: 'equipped',
        label: 'Equipped',
        disabled: true,
      };
    }

    if (isOwned) {
      return {
        type: 'equip',
        label: 'Equip Banner',
        disabled: false,
      };
    }

    if (isTrialActive && !isEquipped) {
      return {
        type: 'equip',
        label: 'Equip Banner',
        disabled: false,
      };
    }

    if (isTrialActive && isEquipped) {
      return {
        type: 'purchase',
        label: `Buy Permanently for ${pricePi} Pi`,
        disabled: false,
      };
    }

    if (canStartTrial) {
      return {
        type: 'trial',
        label: 'Start 7-Day Trial',
        disabled: false,
      };
    }

    return {
      type: 'purchase',
      label: `Buy for ${pricePi} Pi`,
      disabled: false,
    };
  }, [
    canStartTrial,
    isEquipped,
    isOwned,
    isTrialActive,
    pricePi,
  ]);

  if (!isOpen || !banner) {
    return null;
  }

  const runAction = async (
    actionName,
    request,
    options = {}
  ) => {
    if (!bannerId || isWorking) return null;

    const {
      handleWalletErrors = false,
    } = options;

    setWorkingAction(actionName);
    setActionError('');

    try {
      const data = await request();

      return data;
    } catch (error) {
      console.error(
        `Profile banner ${actionName} failed:`,
        error
      );

      if (
        handleWalletErrors &&
        isWalletFundingError(error)
      ) {
        setActionError('Insufficient Pi');
        openQuickTopUpAfterDelay();

        return null;
      }

      setActionError(getErrorMessage(error));

      return null;
    } finally {
      setWorkingAction('');
    }
  };

  const handleSelectBanner = (nextBanner) => {
    if (isWorking || !nextBanner) return;

    const nextBannerId = String(getBannerId(nextBanner));
    const currentBannerId = String(bannerId);

    if (
      !nextBannerId ||
      nextBannerId === currentBannerId
    ) {
      return;
    }

    setActionError('');
    setShowQuickTopUp(false);
    clearQuickTopUpTimer();

    onSelectBanner?.(nextBanner);
  };


const handleStartTrial = async () => {
  const data = await runAction(
    'trial',
    () => startTrial(bannerId)
  );

  if (!data) return;

  const equippedBanner = getResponseBanner(data, {
    ...banner,
    trialActive: true,
    trialExpired: false,
    equipped: true,
    accessType: 'trial',
  });

  await onBannerStateChange?.({
    equippedBanner: {
      ...equippedBanner,
      trialActive: true,
      equipped: true,
    },
    response: data,
    action: 'trial',
  });

  onClose?.();

  navigate('/profile', {
    replace: true,
  });
};


const handlePurchase = async () => {
  const data = await runAction(
    'purchase',
    () => purchaseBanner(bannerId),
    {
      handleWalletErrors: true,
    }
  );

  if (!data) return;

  const equippedBanner = getResponseBanner(data, {
    ...banner,
    owned: true,
    equipped: true,
    accessType: 'purchased',
  });

  await onBannerStateChange?.({
    equippedBanner: {
      ...equippedBanner,
      owned: true,
      equipped: true,
      accessType: 'purchased',
    },
    response: data,
    action: 'purchase',
  });

  onClose?.();

  navigate('/profile', {
    replace: true,
  });
};


const handleEquip = async () => {
  const data = await runAction(
    'equip',
    () => equipBanner(bannerId)
  );

  if (!data) return;

  const equippedBanner = getResponseBanner(data, {
    ...banner,
    equipped: true,
  });

  await onBannerStateChange?.({
    equippedBanner: {
      ...equippedBanner,
      equipped: true,
    },
    response: data,
    action: 'equip',
  });

  onClose?.();

  navigate('/profile', {
    replace: true,
  });
};



  const handleUnequip = async () => {
    const data = await runAction(
      'unequip',
      () => unequipBanner()
    );

    if (!data) return;

    await onBannerStateChange?.({
      equippedBanner: null,
      unequipped: true,
      response: data,
      action: 'unequip',
    });

    onClose?.();
  };

  const handleMainAction = () => {
    if (actionState.disabled || isWorking) return;

    if (actionState.type === 'trial') {
      handleStartTrial();
      return;
    }

    if (actionState.type === 'purchase') {
      handlePurchase();
      return;
    }

    if (actionState.type === 'equip') {
      handleEquip();
    }
  };

  const mainButtonText = () => {
    if (!isWorking) return actionState.label;

    if (workingAction === 'trial') {
      return 'Starting Trial...';
    }

    if (workingAction === 'purchase') {
      return 'Processing Purchase...';
    }

    if (workingAction === 'equip') {
      return 'Equipping...';
    }

    return actionState.label;
  };

  return (
    <>
      <div
        className="profile-banner-drawer-backdrop"
        onClick={onClose}
        role="presentation"
      >
        <section
          className="profile-banner-drawer"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`${banner.name || 'Profile banner'} preview`}
        >
          <div className="profile-banner-drawer-fixed-top">
            <div className="profile-banner-drawer-handle" />

            <div className="profile-banner-drawer-header">
              <div className="profile-banner-drawer-heading">
                <span className="profile-banner-drawer-eyebrow">
                  Profile Banner
                </span>

                <h2>{banner.name || 'Profile Banner'}</h2>
              </div>

              <button
                type="button"
                className="profile-banner-drawer-close"
                onClick={onClose}
                aria-label="Close banner preview"
              >
                ×
              </button>
            </div>
          </div>

          <div className="profile-banner-drawer-scroll">
            <div className="profile-banner-drawer-scroll-inner">
              <div className="profile-banner-drawer-preview">
                <div className="profile-banner-drawer-artwork">
                  <img
                    key={bannerId}
                    src={banner.imageUrl}
                    alt={banner.name || 'Profile banner'}
                  />
                </div>


<div className="profile-banner-drawer-avatar-wrap">
  <img
    src={
      user?.profilePic ||
      storedUser?.profilePic ||
      placeholderProfile
    }
    alt={
      user?.name ||
      storedUser?.name ||
      user?.username ||
      storedUser?.username ||
      'Profile'
    }
    className="profile-banner-drawer-avatar"
  />

  <div className="profile-banner-drawer-user-name">
    {user?.name ||
      storedUser?.name ||
      user?.username ||
      storedUser?.username ||
      'Profile'}
  </div>
</div>


              </div>

              {selectorBanners.length > 1 && (
                <section className="profile-banner-drawer-selector">
                  <div
                    className="profile-banner-drawer-selector-row"
                    role="list"
                    aria-label="Choose another profile banner"
                  >
                    {selectorBanners.map((item) => {
                      const itemId = String(
                        getBannerId(item)
                      );

                      const isSelected =
                        itemId === String(bannerId);

                      const status = getBannerStatus(item);

                      return (
                        <button
                          key={itemId}
                          ref={(element) => {
                            if (element) {
                              selectorItemRefs.current[itemId] =
                                element;
                            } else {
                              delete selectorItemRefs.current[
                                itemId
                              ];
                            }
                          }}
                          type="button"
                          role="listitem"
                          className={`profile-banner-drawer-selector-item ${
                            isSelected ? 'is-selected' : ''
                          }`}
                          onClick={() =>
                            handleSelectBanner(item)
                          }
                          disabled={isWorking}
                          aria-current={
                            isSelected ? 'true' : undefined
                          }
                          aria-label={`Preview ${
                            item?.name || 'profile banner'
                          }`}
                        >
                          <div className="profile-banner-drawer-selector-image">
                            <img
                              src={item?.imageUrl}
                              alt=""
                              loading="lazy"
                            />

                            {status && (
                              <span
                                className={`profile-banner-drawer-selector-status ${status.className}`}
                              >
                                {status.label}
                              </span>
                            )}

                            {isSelected && (
                              <span className="profile-banner-drawer-selector-check">
                                ✓
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              <div className="profile-banner-drawer-content">
                {banner.description && (
                  <p className="profile-banner-drawer-description">
                    {banner.description}
                  </p>
                )}

                <div className="profile-banner-drawer-details">
                  <div className="profile-banner-drawer-detail">
                    <span>Price</span>
                    <strong>{pricePi} Pi</strong>
                  </div>

                  <div className="profile-banner-drawer-detail">
                    <span>Ownership</span>
                    <strong>Permanent</strong>
                  </div>
                </div>

                {canStartTrial && (
                  <div className="profile-banner-drawer-message">
                    <strong>
                      Free 7-day trial available
                    </strong>
                  </div>
                )}

                {isTrialExpired && (
                  <div className="profile-banner-drawer-message">
                    <strong>Trial expired</strong>
                  </div>
                )}

                {actionError && (
                  <div className="profile-banner-drawer-error">
                    {actionError}
                  </div>
                )}

                <div className="profile-banner-drawer-actions">
                  <button
                    type="button"
                    className={`profile-banner-drawer-primary ${
                      actionState.disabled
                        ? 'is-disabled'
                        : ''
                    }`}
                    onClick={handleMainAction}
                    disabled={
                      actionState.disabled || isWorking
                    }
                  >
                    {mainButtonText()}
                  </button>

                  {canStartTrial && (
                    <button
                      type="button"
                      className="profile-banner-drawer-secondary"
                      onClick={handlePurchase}
                      disabled={isWorking}
                    >
                      {workingAction === 'purchase'
                        ? 'Processing Purchase...'
                        : `Buy Permanently for ${pricePi} Pi`}
                    </button>
                  )}

                  {isTrialActive &&
                    !isOwned &&
                    !isEquipped && (
                      <button
                        type="button"
                        className="profile-banner-drawer-secondary"
                        onClick={handlePurchase}
                        disabled={isWorking}
                      >
                        {workingAction === 'purchase'
                          ? 'Processing Purchase...'
                          : `Buy Permanently for ${pricePi} Pi`}
                      </button>
                    )}

                  {isEquipped && (
                    <button
                      type="button"
                      className="profile-banner-drawer-remove"
                      onClick={handleUnequip}
                      disabled={isWorking}
                    >
                      {workingAction === 'unequip'
                        ? 'Removing...'
                        : 'Remove from Profile'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {showQuickTopUp && (
        <QuickTopUpPopup
          open={showQuickTopUp}
          user={{
            username: walletUsername,
          }}
          minAmount={0.1}
          title="Insufficient Pi"
          subtitle={`Top up your BarterFi wallet to purchase ${banner?.name || 'this profile banner'}.`}
          onClose={() => {
            setShowQuickTopUp(false);
            setActionError('');
          }}
          onSuccess={() => {
            setShowQuickTopUp(false);
            setActionError(
              'Wallet topped up. You can try again.'
            );
          }}
        />
      )}
    </>
  );
}

export default ProfileBannerDrawer;
